/**
 * „Co je potřeba udělat“ – úkoly a upozornění odvozené ze zápisů.
 *
 * Stejná pravidla používá úvodní obrazovka (přehled za všechna včelstva),
 * pruh „Pozor“ na kartě včelstva i barva dlaždic (zelená / žlutá / červená).
 * Čisté funkce bez databáze – dají se testovat.
 */
import type { Apiary, Colony, ColonyEvent, ISODate, Queen, TreatmentEvent, VarroaCheckEvent } from './model';
import { summarize, type ColonySummary } from './queries';
import { winterFeeding } from './reports';
import { addDays } from './rules';

export type TaskTone = 'danger' | 'warn' | 'info';

export interface Task {
  tone: TaskTone;
  /** Název ikony z icons.tsx */
  icon: string;
  text: string;
  /** Kam vede ťuknutí */
  to: string;
  colonyId?: string;
  apiaryId?: string;
  /** Jen upozornění (ochranná lhůta) – na úvodní přehled úkolů nepatří. */
  notice?: boolean;
}

export interface TaskOptions {
  today: ISODate;
  seasonStartMonth?: number;
  /** Stáří matky (roky), od kterého upozornit; 0/undefined = vypnuto. */
  queenAgeLimit?: number;
  /** Cíl zakrmení v kg cukru na včelstvo. */
  feedTarget?: number;
}

const fmt = (n: number) => n.toLocaleString('cs-CZ', { maximumFractionDigits: 1 });
const d = (iso: string) => {
  const [y, m, day] = iso.slice(0, 10).split('-').map(Number);
  return `${day}. ${m}.${y !== Number(new Date().getFullYear()) ? ` ${y}` : ''}`;
};
const daysBetween = (a: ISODate, b: ISODate) => Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);

/** Prohlídky se hlídají v sezóně (duben–září); po kolika dnech bez prohlídky upozornit. */
export const INSPECTION_GAP_DAYS = 21;

/** Úkoly jednoho včelstva. `events` = zápisy tohoto včelstva (nesmazané). */
export function colonyTasks(colony: Colony, events: ColonyEvent[], queen: Queen | undefined, o: TaskOptions): Task[] {
  if (colony.status !== 'active' || colony.deletedAt) return [];
  const today = o.today;
  const s: ColonySummary = summarize(events, today, o.seasonStartMonth ?? 1, colony.id);
  const id = colony.id;
  const out: Task[] = [];
  const add = (t: Omit<Task, 'colonyId' | 'apiaryId'>) => out.push({ ...t, colonyId: id, apiaryId: colony.apiaryId });

  if (s.lastVarroa?.alarm)
    add({ tone: 'danger', icon: 'varroa', text: `${s.lastVarroa.fall ? 'Vysoký spad roztočů' : 'Vysoké napadení varroázou'} (${d(s.lastVarroa.date)}) – zvažte ošetření`, to: `vcelstvo/${id}/historie?filtr=varroa` });

  if (s.queenCells)
    add({
      tone: 'warn',
      icon: 'swarm',
      text: `Matečníky při prohlídce ${s.lastInspection ? d(s.lastInspection) : ''} – hlídejte rojovou náladu`,
      to: `vcelstvo/${id}/historie?filtr=inspection`,
    });

  // bez matky: poslední výměna matky byla „bez nové matky“
  const lastQc = events
    .filter((e) => e.type === 'queen_change')
    .sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt))[0];
  if (lastQc && lastQc.type === 'queen_change' && !lastQc.newQueenId && !colony.currentQueenId)
    add({ tone: 'warn', icon: 'queen_change', text: `Bez matky od ${d(lastQc.date)} – zkontrolujte, zda je nová matka`, to: `vcelstvo/${id}/matka` });

  if (queen && o.queenAgeLimit && Number(today.slice(0, 4)) - queen.year >= o.queenAgeLimit)
    add({ tone: 'warn', icon: 'warning', text: `Matka z roku ${queen.year} – zvažte výměnu`, to: `vcelstvo/${id}/upravit/matka` });

  // prohlídky v sezóně (duben–září)
  const month = Number(today.slice(5, 7));
  if (month >= 4 && month <= 9) {
    // dny se počítají nejdřív od 1. 4. – na jaře se nehlásí hned první den sezóny
    const seasonStart = `${today.slice(0, 4)}-04-01`;
    const last = s.lastInspection ?? colony.createdAt?.slice(0, 10);
    const since = last && last > seasonStart ? last : seasonStart;
    if (daysBetween(since, today) > INSPECTION_GAP_DAYS)
      add({
        tone: 'warn',
        icon: 'inspection',
        text:
          s.lastInspection && s.lastInspection >= seasonStart
            ? `Bez prohlídky ${daysBetween(s.lastInspection, today)} dní`
            : s.lastInspection
              ? 'Letos ještě bez prohlídky'
              : 'Zatím bez zapsané prohlídky',
        to: `zapis/novy?typ=inspection&vcelstva=${id}`,
      });
  }

  // konec léčení (vyjmout pásky, ukončit odpařování)
  for (const t of events.filter((e): e is TreatmentEvent => e.type === 'treatment' && !!e.endDate)) {
    const end = t.endDate!;
    if (end >= addDays(today, -3) && end <= addDays(today, 7))
      add({
        tone: end < today ? 'warn' : 'info',
        icon: 'treatment',
        text: end < today ? `Ukončit léčení ${t.productName} (mělo skončit ${d(end)})` : `Ukončit léčení ${t.productName} do ${d(end)}`,
        to: `zapis/${t.id}`,
      });
  }

  // zimní měl čeká na výsledek vyšetření
  for (const v of events.filter((e): e is VarroaCheckEvent => e.type === 'varroa_check' && e.method === 'winter_debris' && e.miteCount == null))
    add({ tone: 'info', icon: 'varroa', text: `Doplnit výsledek vyšetření zimní měli (odebráno ${d(v.date)})`, to: `zapis/${v.id}` });

  if (s.withdrawalUntil)
    add({
      tone: 'info',
      icon: 'hourglass',
      text: `Ochranná lhůta po léčení do ${d(s.withdrawalUntil)} – med zatím nevytáčet`,
      to: `vcelstvo/${id}/historie?filtr=treatment`,
      notice: true,
    });

  // podzimní zakrmení 1. 8. – 31. 10.
  const y = Number(today.slice(0, 4));
  if (o.feedTarget && today >= `${y}-08-01` && today <= `${y}-10-31`) {
    const [r] = winterFeeding([colony], events, y);
    if (r.sugarKg < o.feedTarget)
      add({
        tone: 'info',
        icon: 'feeding',
        text: `Zakrmeno ${fmt(r.sugarKg)} z ${fmt(o.feedTarget)} kg cukru – chybí ${fmt(o.feedTarget - r.sugarKg)} kg`,
        to: `vcelstvo/${id}/historie?filtr=feeding`,
      });
  }
  return out;
}

/** Barva dlaždice včelstva podle jeho úkolů. */
export function colonyTone(tasks: Task[]): 'ok' | 'warn' | 'danger' {
  if (tasks.some((t) => t.tone === 'danger')) return 'danger';
  if (tasks.some((t) => t.tone === 'warn')) return 'warn';
  return 'ok';
}

/**
 * Úkoly za celé stanoviště podle pokynů KVS:
 * zimní měl (1. 1. – 15. 2.) a podzimní ošetření (1. 10. – 31. 12.).
 */
export function apiaryTasks(
  apiary: Apiary,
  colonies: Colony[],
  events: ColonyEvent[] | Map<string, ColonyEvent[]>,
  today: ISODate,
): Task[] {
  const grouped = events instanceof Map ? events : eventsByColony(events);
  const active = colonies.filter((c) => c.apiaryId === apiary.id && c.status === 'active' && !c.deletedAt);
  if (!active.length) return [];
  const y = Number(today.slice(0, 4));
  const md = today.slice(5);
  const out: Task[] = [];
  const has = (c: Colony, pred: (e: ColonyEvent) => boolean) => (grouped.get(c.id) ?? []).some(pred);
  const bulk = (typ: string, ids: string[], extra = '') => `zapis/novy?typ=${typ}&stanoviste=${apiary.id}&vcelstva=${ids.join(',')}${extra}`;

  if (md >= '01-01' && md <= '02-15') {
    const missing = active.filter(
      (c) => !has(c, (e) => e.type === 'varroa_check' && (e.method === 'winter_debris' || !!e.sentToLab) && e.date >= `${y - 1}-11-01`),
    );
    if (missing.length)
      out.push({
        tone: 'warn',
        icon: 'varroa',
        text: `${apiary.name}: odebrat zimní měl k vyšetření (${missing.length} ${missing.length === 1 ? 'včelstvo' : missing.length < 5 ? 'včelstva' : 'včelstev'}) – obvykle do 15. 2., podle pokynů KVS`,
        to: bulk('varroa_check', missing.map((c) => c.id), '&metoda=winter_debris'),
        apiaryId: apiary.id,
      });
  }
  if (md >= '10-01' && md <= '12-31') {
    const missing = active.filter((c) => !has(c, (e) => e.type === 'treatment' && e.date >= `${y}-10-01`));
    if (missing.length)
      out.push({
        tone: 'info',
        icon: 'treatment',
        text: `${apiary.name}: podzimní ošetření proti varroáze podle pokynů KVS (${missing.length} ${missing.length === 1 ? 'včelstvo' : missing.length < 5 ? 'včelstva' : 'včelstev'})`,
        to: bulk('treatment', missing.map((c) => c.id)),
        apiaryId: apiary.id,
      });
  }
  return out;
}

const ORDER: Record<TaskTone, number> = { danger: 0, warn: 1, info: 2 };
export const byUrgency = (a: Task, b: Task) => ORDER[a.tone] - ORDER[b.tone];

/** Zápisy seskupené podle včelstva (jedno projití místo filtrování pro každé včelstvo zvlášť). */
export function eventsByColony(events: ColonyEvent[]): Map<string, ColonyEvent[]> {
  const m = new Map<string, ColonyEvent[]>();
  for (const e of events) {
    if (e.deletedAt) continue;
    for (const id of e.colonyIds) {
      const list = m.get(id);
      if (list) list.push(e);
      else m.set(id, [e]);
    }
  }
  return m;
}
